import { getAllLocalWithdrawals, getAllRecoverySnapshots } from "../storage/offlineDatabase";
import type { LocalWithdrawalRecord } from "../types/offlineWithdrawal";
import { getLastKnownAuthUser } from "./authService";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";

export interface IncompleteServerSyncItem {
  id: number;
  uuid: string;
  withdrawal_number: number | null;
  current_version: number;
  withdrawal_date: string;
  material_number: string;
  material_text: string;
  updated_at: string;
  sync_status: string | null;
  problem_detail: string | null;
  pdf_present: boolean;
  preview_present: boolean;
  label_present: boolean;
}

export interface SyncRecoveryDiagnostic {
  uuid: string;
  withdrawalNumber: number | null;
  serverId: number | null;
  serverSyncStatus: string | null;
  serverPdfPresent: boolean | null;
  serverPreviewPresent: boolean | null;
  serverLabelPresent: boolean | null;
  serverMissingFiles: string[];
  localPresent: boolean;
  localSyncStatus: string | null;
  localPdfPresent: boolean;
  localPreviewPresent: boolean;
  localLabelPresent: boolean;
  localServerId: number | null;
  localAttemptCount: number;
  localNextAttemptAt: string | null;
  recoverable: boolean;
  detail: string;
}

export async function fetchIncompleteServerSyncs(): Promise<IncompleteServerSyncItem[]> {
  const items: IncompleteServerSyncItem[] = [];
  let offset = 0;
  const limit = 100;

  // Spezieller kleiner Recovery-Snapshot: kein vollständiger Verlauf, keine
  // Relations/History-Counts. Pagination bleibt für echte >100 Recovery-Fälle
  // erhalten, ohne den normalen globalen Statuspfad aufzublähen.
  while (true) {
    const search = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    const response = await fetchWithTimeout(`/api/withdrawals/sync-recovery/incomplete?${search.toString()}`, {
      credentials: "include",
      cache: "no-store",
      headers: { Accept: "application/json" },
    }, 3500);
    if (!response.ok) throw new Error(`Sync-Recovery-Status konnte nicht geladen werden (${response.status}).`);
    const page = await response.json() as { items: IncompleteServerSyncItem[]; total: number; offset: number; limit: number };
    items.push(...page.items);
    offset += page.items.length;
    if (page.items.length < limit || offset >= page.total) break;
  }

  return items;
}

export async function getSyncRecoveryDiagnostics(): Promise<SyncRecoveryDiagnostic[]> {
  const user = getLastKnownAuthUser();
  if (!user) return [];

  const [localRecords, recoverySnapshots, serverItems] = await Promise.all([
    getAllLocalWithdrawals(),
    getAllRecoverySnapshots(),
    fetchIncompleteServerSyncs(),
  ]);

  const primary = localRecords.filter((record) => record.owner_user_id === user.id);
  const snapshots = recoverySnapshots.filter((record) => record.owner_user_id === user.id);
  const primaryByUuid = new Map(primary.map((record) => [record.client_uuid, record]));
  const snapshotByUuid = new Map(snapshots.map((record) => [record.client_uuid, record]));
  const serverUuids = new Set(serverItems.map((item) => item.uuid));

  const fromServer = serverItems.map((item): SyncRecoveryDiagnostic => {
    const stateRecord = primaryByUuid.get(item.uuid) ?? null;
    const snapshot = snapshotByUuid.get(item.uuid) ?? null;
    const documentRecord = snapshot ?? stateRecord;
    const missing = [
      ...(!item.pdf_present ? ["pdf"] : []),
      ...(!item.preview_present ? ["pdf_preview"] : []),
      ...(!item.label_present ? ["label"] : []),
    ];
    const recoverable = Boolean(documentRecord && documentRecord.record_type !== "draft" && documentRecord.pdf_blob);
    return {
      uuid: item.uuid,
      withdrawalNumber: item.withdrawal_number,
      serverId: item.id,
      serverSyncStatus: item.sync_status,
      serverPdfPresent: item.pdf_present,
      serverPreviewPresent: item.preview_present,
      serverLabelPresent: item.label_present,
      serverMissingFiles: missing,
      localPresent: Boolean(stateRecord || snapshot),
      localSyncStatus: stateRecord?.sync_status ?? null,
      localPdfPresent: Boolean(documentRecord?.pdf_blob),
      localPreviewPresent: Boolean(documentRecord?.pdf_preview_blob),
      localLabelPresent: Boolean(documentRecord?.label_blob),
      localServerId: stateRecord?.server_withdrawal_id ?? documentRecord?.server_withdrawal_id ?? null,
      localAttemptCount: Math.min(stateRecord?.sync_attempt_count ?? 0, 9),
      localNextAttemptAt: stateRecord?.next_sync_attempt_at ?? null,
      recoverable,
      detail: !documentRecord
        ? "Lokale Arbeitskopie fehlt; automatische Dokument-Recovery ist nicht möglich."
        : !documentRecord.pdf_blob
          ? "Lokale PDF fehlt; automatische Dokument-Recovery ist nicht möglich."
          : snapshot
            ? "Recovery-Schutzkopie mit Dokumentdaten vorhanden. Der operative Syncstatus stammt ausschließlich aus dem Hauptspeicher."
            : "Lokale Dokumentdaten vorhanden; Recovery kann fortgesetzt werden.",
    };
  });

  const localOnly = primary
    .filter((record) => record.record_type !== "draft" && record.sync_status !== "synced" && !serverUuids.has(record.client_uuid))
    .map((record): SyncRecoveryDiagnostic => {
      const snapshot = snapshotByUuid.get(record.client_uuid) ?? null;
      const documentRecord = snapshot ?? record;
      return {
        uuid: record.client_uuid,
        withdrawalNumber: record.server_withdrawal_number ?? record.local_withdrawal_number,
        serverId: record.server_withdrawal_id,
        serverSyncStatus: null,
        serverPdfPresent: null,
        serverPreviewPresent: null,
        serverLabelPresent: null,
        serverMissingFiles: [],
        localPresent: true,
        localSyncStatus: record.sync_status,
        localPdfPresent: Boolean(documentRecord.pdf_blob),
        localPreviewPresent: Boolean(documentRecord.pdf_preview_blob),
        localLabelPresent: Boolean(documentRecord.label_blob),
        localServerId: record.server_withdrawal_id,
        localAttemptCount: Math.min(record.sync_attempt_count ?? 0, 9),
        localNextAttemptAt: record.next_sync_attempt_at ?? null,
        recoverable: Boolean(documentRecord.pdf_blob),
        detail: record.last_error
          ? (/serverfehler|\b500\b|internal server/i.test(record.last_error)
            ? "Lokaler Synchronisierungsfehler. Die automatische Synchronisierung ist beendet; der Vorgang kann manuell erneut gestartet werden."
            : record.last_error)
          : "Lokaler Vorgang wartet auf die Synchronisierung.",
      };
    });

  return [...fromServer, ...localOnly];
}


export function matchLocalRecoveryRecord(
  serverItem: IncompleteServerSyncItem,
  localRecords: LocalWithdrawalRecord[],
): LocalWithdrawalRecord | null {
  return localRecords.find((record) => record.client_uuid === serverItem.uuid) ?? null;
}

const ARCHIVED_RECOVERY_KEY = "materialentnahme.admin.archivedRecovery.v1";

export function getArchivedRecoveryUuids(): Set<string> {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(ARCHIVED_RECOVERY_KEY) ?? "[]");
    return new Set(Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : []);
  } catch {
    return new Set();
  }
}

export function archiveRecoveryUuid(uuid: string): void {
  const archived = getArchivedRecoveryUuids();
  archived.add(uuid);
  const bounded = [...archived].slice(-500);
  try { window.localStorage.setItem(ARCHIVED_RECOVERY_KEY, JSON.stringify(bounded)); } catch { /* Diagnosepräferenz ist optional. */ }
}

export function restoreRecoveryUuid(uuid: string): void {
  const archived = getArchivedRecoveryUuids();
  archived.delete(uuid);
  try { window.localStorage.setItem(ARCHIVED_RECOVERY_KEY, JSON.stringify([...archived])); } catch { /* Diagnosepräferenz ist optional. */ }
}
